import { BuildClass, Do, fsmoData } from '../../../../universal';
import { React } from '../../../lib';
import { FormButtonSet } from '../buttons';
import { CJSX, useStateSync } from '../meta-types';
import { FormFillButton } from './llm-fill';
import { FormManager, FormSaveOutcome } from './manager';
const FormComponentInner = (props, ref) => {
    const manager = new FormManager({
        fields: props.fields,
        formFixer: props.formFixer,
        disableFormFill: props.disableFormFill,
        getDefaultValues: props.defaultValue,
    });
    const [state, setState] = React.useState(props.value ?? manager.defaultState);
    useStateSync({
        propVal: props.value ?? manager.defaultState,
        setProp: props.onUpdate,
        stateVal: state,
        setState: setState,
    });
    const refFormElement = React.useRef(null);
    const [, setFocused] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [message, setMessageRaw] = React.useState({
        msg: '',
        type: 'bad',
    });
    const timerRef = React.useRef(null);
    const setMessage = (msg, type) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setMessageRaw({
            msg: msg,
            type: type ?? 'bad',
        });
    };
    const hasChanged = manager.hasChanges(state);
    const onChangeState = props.onChangeState;
    React.useEffect(() => {
        onChangeState?.({ hasChanges: hasChanged, isLoading });
    }, [hasChanged, isLoading, onChangeState]);
    React.useEffect(() => {
        props.onMount?.();
    }, []);
    const components = fsmoData(props.fields, {
        map: (_field, key) => [
            key,
            manager.renderComponentFormRow({
                currentState: state,
                key: key,
                lblWidth: props.lblWidth,
                highlightChanges: !props.disableChangeHighlights,
                disabled: props.disabled ?? false,
                onUpdate: v => {
                    setState(v);
                    setMessage('');
                },
                onFocus: () => {
                    setFocused(key);
                },
                onBlur: () => {
                    setFocused(null);
                },
            }),
        ],
        valuesOnly: true,
    });
    React.useImperativeHandle(ref, () => ({
        manager,
        currentState: state,
        setCurrentState: setState,
        setFocused,
        hasUnsavedChanges: hasChanged,
    }));
    const buttons = !props.excludeButtons && (React.createElement(FormButtonSet, { loading: isLoading, msg: message.msg, tick: message.type == 'good', neutral: message.type == 'neutral', widths: props.buttonWidth, buttons: [
            {
                lbl: props.lblSave ?? 'Save',
                type: 'submit',
                title: hasChanged
                    ? (props.lblSaveTooltip ?? 'Save changes')
                    : 'Nothing to save',
                onClick: () => {
                },
            },
            {
                lbl: 'Reset',
                hidden: props.excludeReset ?? false,
                title: hasChanged ? 'Resets the form' : 'Nothing to reset',
                disabled: !hasChanged,
                onClick: () => {
                    setState(manager.defaultState);
                },
            },
            ...(props.extraActions ?? []).map(x => ({
                lbl: x.lbl,
                onClick: () => {
                    x.onClick({
                        state: manager.getPublicState(state),
                        setLoading: setIsLoading,
                        setMessage: setMessage,
                        markAsSuccessful: () => {
                            setMessage('', 'good');
                            if (timerRef.current) {
                                clearTimeout(timerRef.current);
                            }
                            timerRef.current = Number(setTimeout(() => {
                                setMessage('');
                            }, 2000));
                            props.onSuccessfulSave?.();
                        },
                    });
                },
                type: x.type,
                disabled: Do(() => {
                    if (x.requiresChanges && !hasChanged) {
                        return true;
                    }
                    if (x.requiresNoChanges && hasChanged) {
                        return true;
                    }
                    return x.checkIfUsable?.(manager.getPublicState(state)) ?? false;
                }),
            })),
        ] }));
    const submitEvent = async () => {
        setIsLoading(true);
        setMessage('');
        const result = await manager.trySaving({
            allowSavingWithoutChanges: props.allowSavingWithoutChanges ?? false,
            currentState: state,
            setState: setState,
            onSave: props.onSave,
        });
        setIsLoading(false);
        if (result.status == FormSaveOutcome.Error) {
            setMessage(result.message);
            return;
        }
        if (result.status == FormSaveOutcome.Neutral) {
            setMessage(result.message, 'neutral');
            timerRef.current = Number(setTimeout(() => {
                setMessage('');
            }, 1000));
            return;
        }
        setMessage(result.message, 'good');
        timerRef.current = Number(setTimeout(() => {
            setMessage('');
        }, 4000));
        props.onSuccessfulSave?.();
    };
    return (React.createElement("form", { tabIndex: -1, ref: refFormElement, className: BuildClass({
            [props.className ?? '']: true,
            'font-condensed relative': true,
        }), action: undefined, onSubmit: e => {
            e.preventDefault();
            submitEvent().catch(console.error);
        }, onKeyDown: e => {
            if (e.ctrlKey && e.code === 'KeyS') {
                e.stopPropagation();
                e.preventDefault();
                submitEvent().catch(console.error);
                return;
            }
            manager.hotkeyPress(e, {
                currentState: state,
                setState: setState,
                formPrompt: props.formPrompt,
                voicePrompt: props.voicePrompt,
                containingElement: refFormElement.current ?? document.body,
            });
        }, onKeyUp: e => {
            manager.hotkeyRelease(e);
        } },
        props.contentClassName ? (React.createElement("div", { className: BuildClass(props.contentClassName) }, components)) : (components),
        buttons,
        React.createElement(CJSX, { cond: !props.disableFormFill },
            React.createElement("div", { className: BuildClass({
                    'absolute right-0': true,
                    'bottom-8': !(props.excludeButtons ?? false),
                    '-bottom-8': props.excludeButtons ?? false,
                }) },
                React.createElement(FormFillButton, { manager: manager, currentState: state, setState: setState, formPrompt: props.formPrompt, voicePrompt: props.voicePrompt, containingElement: refFormElement.current ?? document.body })))));
};
export const FormComponent = React.forwardRef(FormComponentInner);
//# sourceMappingURL=render.js.map